import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'

import { SidebarConfigProps } from '@/reducers/sidebar.slice'
import { User } from '@/reducers/auth/userReducer'
import { RailRestructured } from './rail/RailRestructured'
import { ExpandedRestructured } from './expanded/ExpandedRestructured'
import { NavAnnouncementProps } from './expanded/NavListRestructured'

export type {
  NavAnnouncementProps,
  NavAnnouncementStep,
} from './expanded/NavListRestructured'
import {
  DeviceWidthCombo,
  useIsDeviceWidth,
  useIsMobileTakeover,
} from '@/utils/deviceWidthHelpers'

interface SidebarProps {
  device: string
  dropdownOptions: Array<{ icon: IconProp; onClick: () => void; text: string }>
  categories?: Record<string, SidebarConfigProps> | null
  user?: User | null
  footerBanner?: React.ReactNode
  forceCollapse?: boolean
  onAskHaven?: () => void
  navAnnouncement?: NavAnnouncementProps
}

export const Sidebar = ({
  device,
  dropdownOptions,
  categories,
  user,
  footerBanner,
  forceCollapse,
  onAskHaven,
  navAnnouncement,
}: SidebarProps) => {
  const [isOpen, setIsOpen] = React.useState(true)
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    null
  )
  const isSmallDevice = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  const isMobile = useIsMobileTakeover()
  // Collapse automatically on small devices
  useEffect(() => {
    if (isSmallDevice) {
      setIsOpen(false)
    }
  }, [isSmallDevice])

  const { pathname } = useLocation()
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false)
    }
  }, [pathname, isMobile])

  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (forceCollapse) {
      // Capture current state via functional updater so we don't need isOpen
      // in the dependency array (adding it would cause an infinite loop because
      // the effect also calls setIsOpen).
      setIsOpen((prev) => {
        wasOpenRef.current = prev
        return false
      })
    } else if (wasOpenRef.current && !isSmallDevice) {
      // Skip the restore on a small device: the collapse effect above only ever
      // closes, and it will not re-fire while `isSmallDevice` is unchanged, so
      // restoring here would leave a 300px sidebar in a narrow viewport with
      // both the coachmark and the mobile card on screen.
      setIsOpen(true)
      wasOpenRef.current = false
    }
  }, [forceCollapse, isSmallDevice])

  let sidebarWidthClass = 'w-[68px]'
  if (isOpen) {
    sidebarWidthClass = 'w-[300px]'
    if (isMobile) {
      sidebarWidthClass = 'relative z-50 w-screen'
    }
  }

  return (
    <div
      // Lets a fixed-position footer elsewhere in the app measure this element's
      // rendered width instead of reconstructing it from the sidebar's state.
      data-sidebar-root=""
      className={`transition-[width] duration-300 ease-in-out overflow-hidden shrink-0 ${sidebarWidthClass}`}
      onClickCapture={(event) => {
        // On mobile a tap on the collapsed rail opens the full menu instead of
        // navigating; rail actions marked data-rail-passthrough still fire.
        if (
          isMobile &&
          !isOpen &&
          !(
            event.target instanceof Element &&
            event.target.closest('[data-rail-passthrough]')
          )
        ) {
          event.preventDefault()
          event.stopPropagation()
          setIsOpen(true)
        }
      }}
      onClick={(event) => {
        if (
          isMobile &&
          isOpen &&
          event.target instanceof Element &&
          event.target.closest('a')
        ) {
          setIsOpen(false)
        }
      }}
      role="presentation"
    >
      {isOpen ? (
        <ExpandedRestructured
          device={device}
          footerOptions={dropdownOptions}
          categories={categories}
          isOpen={isOpen}
          setIsOpen={() => setIsOpen(false)}
          user={user}
          selectedCategory={selectedCategory}
          isSmallDevice={isSmallDevice}
          footerBanner={footerBanner}
          onAskHaven={onAskHaven}
          navAnnouncement={navAnnouncement}
        />
      ) : (
        <RailRestructured
          device={device}
          dropdownOptions={dropdownOptions}
          categories={categories}
          isOpen={isOpen}
          setIsOpen={() => setIsOpen(true)}
          onCategoryClick={(key) => {
            setSelectedCategory(key)
            setIsOpen(true)
          }}
          onAskHaven={onAskHaven}
        />
      )}
    </div>
  )
}
